import moment from 'moment';

import BenchlingConfig from 'BenchlingConfig';
import {isInStore, hasUnsavedChanges} from 'notebook/entries/BaseEntryUtil';
import * as DocumentEditorLockReduxActions from 'notebook/realtime/DocumentEditorLockReduxActions';
import {DocumentEditorLock} from 'notebook/realtime/reduceDocumentEditorLockState';
import {isCurrentClient} from 'realtime/util/RealtimeUtil';
import {BenchlingState} from 'redux-stores/reduceApp';
import {getStore} from 'redux-stores/Store';

export const RELEASE_LOCK_TOMBSTONE = -1;
export const releaseLockTimeoutMap: Record<string, NodeJS.Timeout | typeof RELEASE_LOCK_TOMBSTONE> = {};

const DocumentEditorLockUtil = {
  /**
   * Schedules a timeout for releasing the lock for a given entry/document corresponding to the API ID provided.
   *
   * @param documentId -- the API identifier of the Entry/Document we are trying to release
   */
  releaseLock(documentId: string): void {
    if (
      !(
        BenchlingConfig.ELN_RELEASE_DOCUMENT_LOCK_INTERVAL > -1 &&
        releaseLockTimeoutMap[documentId] !== RELEASE_LOCK_TOMBSTONE &&
        isInStore(documentId) &&
        !hasUnsavedChanges(documentId)
      )
    ) {
      return;
    }

    DocumentEditorLockUtil.clearReleaseLockTimeout(documentId);
    releaseLockTimeoutMap[documentId] = setTimeout(() => {
      // Make sure the document hasn't been modified in the timeout period
      if (isInStore(documentId) && !hasUnsavedChanges(documentId)) {
        getStore().dispatch(DocumentEditorLockReduxActions.releaseLock(documentId));
      }
    }, BenchlingConfig.ELN_RELEASE_DOCUMENT_LOCK_INTERVAL);
  },

  clearReleaseLockTimeout(documentId: string): void {
    if (releaseLockTimeoutMap[documentId]) {
      // If the value exists and it is not a tombstone value, its a timeout so clear it
      if (releaseLockTimeoutMap[documentId] !== RELEASE_LOCK_TOMBSTONE) {
        clearTimeout(releaseLockTimeoutMap[documentId]);
      }
      delete releaseLockTimeoutMap[documentId];
    }
  },

  forceAcquireLock(documentId: string): void {
    if (isInStore(documentId)) {
      // If we are force acquiring the lock, we want to tombstone the key on the release map to block any
      // other async processes that might try to prematurely release the lock
      DocumentEditorLockUtil.clearReleaseLockTimeout(documentId);
      releaseLockTimeoutMap[documentId] = RELEASE_LOCK_TOMBSTONE;
      getStore().dispatch(DocumentEditorLockReduxActions.acquireLock(documentId, true /* forceNetwork */));
    }
  },

  acquireLock(documentId: string): void {
    if (isInStore(documentId)) {
      DocumentEditorLockUtil.clearReleaseLockTimeout(documentId);
      getStore().dispatch(DocumentEditorLockReduxActions.acquireLock(documentId));
    }
  },

  getLock(documentId: string): DocumentEditorLock | undefined {
    return isInStore(documentId) ? getStore().getState().ui.documentEditorLockState[documentId] : undefined;
  },

  isLockedInRealtime(documentEditorLock?: DocumentEditorLock): boolean {
    if (documentEditorLock && !isCurrentClient(documentEditorLock.clientId)) {
      if (moment().isBefore(moment(documentEditorLock.expiresAt))) {
        return true;
      }
    }
    return false;
  },

  mapDocumentEditorLockStateToProps(
    state: BenchlingState,
    fileId: string
  ): {documentEditorLock?: DocumentEditorLock} {
    const documentEditorLock = state.ui.documentEditorLockState[fileId];
    if (documentEditorLock) {
      return {
        documentEditorLock,
      };
    }
    return {};
  },
};

export default DocumentEditorLockUtil;
